import {AllActivityTypesArray} from "../types-enums/activity";
import {
    black,
    calculateBeginningOfContent,
    calculateFirstRowPosition,
    calculateHeightOfTitleAndBody,
    conditionallyAddPageToJourneyPdf,
    drawActivityTotalEmissions,
    drawBody,
    drawEmissionsSummary,
    drawJourneyInformation,
    drawRowTitle,
    generateBodyText,
    generateDateAndDayString,
    LogoProps,
    setFooter,
    setHeader,
    setupGeneralPdfProps,
    setupPdfFonts,
    setUpPdfPageMargins,
    SPACER_TO_CO2_KG_E
} from "./utils";
import {i18n} from "i18next";
import {PDFDocument} from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import {createArrayFromNumber, DISPLAY_ORDER_TRAVEL_OPTIONS} from "../utils/common";
import {getActivitiesByDay, getActivitiesByTravelOptionType} from "../utils/getter/activities";
import {getCo2EForDayOfJourney} from "../utils/getter/co2-getter";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL} from "../api/model/common";
import {DurationProps} from "../types-enums/activity-common-properties";
import {getBackendCalculationStatus} from "../utils/getter/emissions";
import {getDurationInDaysOrNights} from "../utils/getter/common";
import {Customer} from "../api/model/customer";

/**
 * Asynchronously creates a PDF document summarizing a journey with activities, travel details,
 * agency and user information, and emissions data. The generated PDF includes structured content
 * organized into sections, such as headers, footers, journey metadata, and per-day activity breakdowns.
 *
 * The function:
 * - Configures PDF metadata like subject and title.
 * - Sets up fonts, page margins, and reusable layout properties.
 * - Generates a header featuring agency, user contact details, and a logo.
 * - Dynamically constructs pages to accommodate varying journey durations and activity counts.
 * - Organizes activities into sections by day and travel option type.
 * - Renders CO2 emissions data and textual descriptions of activities.
 * - Adds visual components such as titles, footers, and summarized emissions reports.
 *
 * Layout adjusts automatically to prevent content overflow, dynamically adding pages as needed and
 * recalculating positions for subsequent content. External utility functions are used for tasks such
 * as font loading, calculating content heights, and formatting emissions data.
 *
 * @param {AllActivityTypesArray} activities - Array of all activity objects for the journey.
 * @param {i18n} i18n - Localization configuration, providing translation functions and resolved language.
 * @param {string} agencyName - Name of the agency responsible for planning the journey.
 * @param {string} agencyAddress - Address of the agency.
 * @param {string} userName - Name of the user for whom the PDF is being generated.
 * @param {string} email - Email address of the user for contact purposes.
 * @param {string} completePhoneNumber - Complete phone number (including country code) of the user.
 * @param {LogoProps} logo - Object containing the binary data and properties for the agency's logo.
 * @param {DurationProps} journeyDuration - Object representing the journey's duration in days and/or nights.
 * @param {string | number | null} name - Name or title of the journey. Can be null.
 * @param {string | null} description - Description of the journey. Can be null.
 * @param {Customer | null} customer - Customer details associated with the journey. Can be null.
 * @param {number} personCount - Number of people included in the journey.
 *
 * @returns {Promise<Uint8Array>} A promise that resolves to a Uint8Array representing the binary
 * data of the generated PDF.
 */
export const createJourneyPdf = async (
    activities: AllActivityTypesArray,
    i18n: i18n,
    agencyName: string,
    agencyAddress: string,
    userName: string,
    email: string,
    completePhoneNumber: string,
    logo: LogoProps,
    journeyDuration: DurationProps,
    name: string | number | null,
    description: string | null,
    customer: Customer | null,
    personCount: number,
) => {
    const {resolvedLanguage, t} = i18n
    const pdfDoc = await PDFDocument.create()
    pdfDoc.setSubject('PDF.TRAVEL_COMPONENTS.PDF_SUBJECT');
    pdfDoc.setTitle(t("PDF.TRAVEL_COMPONENTS.PDF_TITLE"));
    pdfDoc.registerFontkit(fontkit)
    let page = pdfDoc.addPage()
    let pageNumber = 1
    const {fontBold, fontNormal} = await setupPdfFonts(pdfDoc)
    const {pageMarginLeft, pageMarginRight} = setUpPdfPageMargins()
    const {width: pageWidth, height: pageHeight} = page.getSize()
    const headerHeight = await setHeader(
        page,
        pageHeight,
        pageWidth,
        fontNormal,
        fontBold,
        pageMarginLeft,
        pageMarginRight,
        i18n,
        pdfDoc,
        userName,
        agencyName,
        agencyAddress,
        logo,
        email,
        completePhoneNumber
    )
    const {
        footerHeight,
        titleFontSize,
        marginFromBodyToTitle,
        rowGapForJourneyPdf: rowGap,
        bodyFontSize,
        bodyLineHeight,
        titleLineHeight,
        totalHeaderHeightWithMargin,
        heightForContent,
        marginFromActivitiesToTravelOptionType,
        marginJourneyPropsToActivities
    } = setupGeneralPdfProps(headerHeight, pageHeight)
    setFooter(page, pageHeight, pageWidth, fontNormal, fontBold, footerHeight, pageMarginLeft, pageMarginRight, i18n)

    const journeyTitleAndDescriptionHeight = drawJourneyInformation(
        name,
        description,
        customer,
        personCount,
        journeyDuration,
        i18n,
        page,
        pageMarginLeft,
        pageMarginRight,
        calculateBeginningOfContent(pageHeight, totalHeaderHeightWithMargin),
        bodyFontSize,
        bodyLineHeight,
        titleFontSize,
        titleLineHeight,
        fontNormal,
        fontBold,
        pageWidth,
        marginJourneyPropsToActivities
    )
    let {
        heightLeftOver,
        firstRowTitleYPosition: dayTitleYPosition,
        firstBodyYPosition: travelOptionYPosition,
        originalDayTitleYPosition: firstRowTitleYPositionWithoutJourneyTitleAndDescription,
    } = calculateFirstRowPosition(
        pageHeight,
        totalHeaderHeightWithMargin,
        marginFromBodyToTitle,
        heightForContent,
        journeyTitleAndDescriptionHeight
    )

    const originalDayTitleYPosition = firstRowTitleYPositionWithoutJourneyTitleAndDescription
    const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
    if (journeyDurationInDays !== null) {
        createArrayFromNumber(journeyDurationInDays).forEach(/**
         * Generates and renders the visual representation of activities for a specific day,
         * including titles, travel options, and activities within those travel options. Adjusts
         * y-coordinate positions dynamically to ensure content fits within the page limits.
         *
         * This method performs the following tasks:
         * - Retrieves activities for a given day.
         * - Draws the title for the day with a formatted date, day, and CO2 emissions information.
         * - Iterates through predefined travel option types, rendering their respective sections.
         * - Draws text for each activity under the relevant travel option type, including calculations
         *   for total emissions and status verification.
         * - Handles page overflow by conditionally adding new pages when needed to accommodate
         *   additional content.
         *
         * Dependencies:
         * Utilizes several external functions and constants for tasks such as:
         * - Retrieving activities grouped by day or travel option type.
         * - Generating and drawing text content and emissions data.
         * - Calculating heights for titles, bodies, and adjusting y-positions for rendering.
         *
         * Note:
         * The method updates y-positions and page-related states dynamically to ensure smooth
         * layout transitions between content blocks and pages.
         *
         * @param {number} day - The day of the journey for which content is being rendered.
         */
        day => {
            const activitiesAtDay = getActivitiesByDay(activities, day)
            drawRowTitle(
                i18n,
                page,
                pageMarginLeft,
                titleFontSize,
                dayTitleYPosition,
                fontBold,
                pageWidth,
                pageMarginRight,
                fontNormal,
                generateDateAndDayString(journeyDuration, day, i18n),
                getCo2EForDayOfJourney(activities, day, true),
                resolvedLanguage
            )
            dayTitleYPosition = dayTitleYPosition - titleFontSize - marginFromBodyToTitle
            heightLeftOver = heightLeftOver - titleFontSize - marginFromBodyToTitle
            let lastActivityHeight = 0
            DISPLAY_ORDER_TRAVEL_OPTIONS.forEach(travelOptionType => {
                const activitiesPerTravelOption = getActivitiesByTravelOptionType(activitiesAtDay, travelOptionType)
                let activitiesYPosition = 0
                lastActivityHeight = 0
                if (activitiesPerTravelOption.length > 0) {
                    page.drawText(t(`GENERAL.${travelOptionType.toUpperCase()}`), {
                        x: pageMarginLeft,
                        y: travelOptionYPosition,
                        size: bodyFontSize,
                        font: fontBold,
                        color: black,
                    })
                    activitiesYPosition = travelOptionYPosition - marginFromActivitiesToTravelOptionType
                    heightLeftOver = heightLeftOver - bodyFontSize - marginFromActivitiesToTravelOptionType
                    activitiesPerTravelOption.forEach((activity, index) => {
                        const {common: {totalEmissions}} = activity
                        const status = getBackendCalculationStatus(totalEmissions)
                        if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) {
                            const {
                                co2Text,
                                bodyText,
                                textLineCount,
                                totalTextLinesCount
                            } = generateBodyText(
                                activity,
                                activities,
                                i18n,
                                fontNormal,
                                bodyFontSize,
                                pageWidth - pageMarginRight - pageMarginLeft - SPACER_TO_CO2_KG_E,
                                " ",
                                true,
                                false
                            )

                            const {totalBodyHeight} = calculateHeightOfTitleAndBody(
                                totalTextLinesCount,
                                bodyLineHeight,
                                titleFontSize,
                                marginFromBodyToTitle
                            )
                            heightLeftOver -= totalBodyHeight

                            const result = conditionallyAddPageToJourneyPdf(
                                heightLeftOver,
                                totalBodyHeight,
                                footerHeight,
                                pdfDoc,
                                page,
                                pageHeight,
                                pageWidth,
                                fontNormal,
                                fontBold,
                                pageMarginLeft,
                                pageMarginRight,
                                i18n,
                                userName,
                                agencyName,
                                agencyAddress,
                                logo,
                                email,
                                completePhoneNumber,
                                heightForContent,
                                activitiesYPosition,
                                originalDayTitleYPosition,
                            )
                            if (result.page !== page) pageNumber++
                            activitiesYPosition = result.activitiesYPosition
                            heightLeftOver = result.heightLeftOver
                            page = result.page
                            lastActivityHeight = totalBodyHeight
                            let co2EBodyYPosition = activitiesYPosition
                            drawBody(
                                page,
                                pageMarginLeft,
                                activitiesYPosition,
                                bodyFontSize,
                                bodyLineHeight,
                                fontNormal,
                                `${index + 1}. ${bodyText}`,
                            )
                            co2Text.forEach(text => {
                                drawActivityTotalEmissions(
                                    activity,
                                    co2EBodyYPosition,
                                    page,
                                    bodyFontSize,
                                    bodyLineHeight,
                                    pageWidth,
                                    pageMarginRight,
                                    fontNormal,
                                    text,
                                )
                                co2EBodyYPosition = co2EBodyYPosition - textLineCount[index] * bodyLineHeight
                            })
                            activitiesYPosition = activitiesYPosition - totalBodyHeight
                        }
                    })
                    travelOptionYPosition = activitiesYPosition
                }
            })
            dayTitleYPosition = travelOptionYPosition - lastActivityHeight - 2 * rowGap
            travelOptionYPosition = dayTitleYPosition - marginFromBodyToTitle
        })
    }
    drawEmissionsSummary(
        activities,
        pageWidth,
        bodyLineHeight,
        bodyFontSize,
        titleFontSize,
        marginFromBodyToTitle,
        pdfDoc,
        heightLeftOver,
        footerHeight,
        rowGap,
        page,
        pageHeight,
        fontNormal,
        fontBold,
        pageMarginLeft,
        pageMarginRight,
        i18n,
        userName,
        agencyName,
        agencyAddress,
        logo,
        email,
        completePhoneNumber,
        heightForContent,
        dayTitleYPosition,
        travelOptionYPosition,
        originalDayTitleYPosition,
        "perTravelOption",
        journeyDuration
    )

    return await pdfDoc.save()
}